import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import { parseFileName } from './api';

export type Verdict =
  | 'erfuellt'
  | 'nicht_erfuellt'
  | 'nicht_pruefbar'
  | 'nicht_anwendbar';
export const VERDICTS: Verdict[] = [
  'erfuellt',
  'nicht_erfuellt',
  'nicht_pruefbar',
  'nicht_anwendbar',
];
export type Recommendation =
  | 'empfohlen'
  | 'unter_vorbehalt'
  | 'nicht_empfohlen';
export type CaseStatus =
  | 'analysing'
  | 'ready'
  | 'failed'
  | 'approved'
  | 'rejected';
export type SignOff = 'freigegeben' | 'abgelehnt';

export interface EvidenceRow {
  sheet: string;
  id: string;
  kriteriumId: string;
  fields: Record<string, string>;
}

export interface Criterion {
  id: string;
  name: string;
  sollwert: string;
  nachweis: string;
  grundlage: string;
  verdict: Verdict;
  /** `ki`: judged by the model, `regel`: follows from the data, `fehler`: the model failed. */
  quelle: 'ki' | 'regel' | 'fehler';
  begruendung: string;
  belege: string[];
  offeneSchritte: string[];
  evidence: EvidenceRow[];
}

export interface Assessment {
  auftrag: Record<string, string>;
  kriterien: Criterion[];
  zusammenfassung: {
    gesamt: number;
    erfuellt: number;
    nichtErfuellt: number;
    nichtPruefbar: number;
    nichtAnwendbar: number;
    erfuellungsgrad: number;
    empfehlung: Recommendation;
  };
  model?: string;
  erstelltAm?: string;
}

export interface Decision {
  verdict: Verdict;
  kommentar?: string;
}

export interface Review {
  entscheidungen: Record<string, Decision>;
  abnahme?: {
    entscheidung: SignOff;
    kommentar?: string;
    pruefer: string;
    zeitpunkt: string;
  };
}

export interface CaseSummary {
  id: string;
  room: string;
  orderNumber?: string;
  status: CaseStatus;
  empfehlung?: Recommendation;
  createdBy: string;
  createdAt: string;
  reviewedBy?: string;
  reviewerName?: string;
  reviewedAt?: string;
}

export interface CaseDetail extends CaseSummary {
  error?: string;
  catalogName: string;
  checklistName: string;
  assessment?: Assessment;
  review?: Review;
}

export interface AccessibilityAccess {
  allowed: boolean;
  maxUploadBytes: number;
  formats: string[];
}

/** Error with a message from the backend that can be shown to the user as is. */
export class AccessibilityError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Per-field problems of a sign-off, e.g. `KR-02: Entscheidung fehlt.` */
    readonly details: string[] = [],
  ) {
    super(message);
    this.name = 'AccessibilityError';
  }
}

export interface AccessibilityApi {
  getAccess(): Promise<AccessibilityAccess>;
  listCases(): Promise<CaseSummary[]>;
  createCase(catalog: File, checklist: File, room: string): Promise<string>;
  getCase(id: string): Promise<CaseDetail>;
  reanalyze(id: string): Promise<void>;
  saveReview(
    id: string,
    entscheidungen: Record<string, Decision>,
  ): Promise<CaseDetail>;
  signOff(
    id: string,
    input: {
      entscheidung: SignOff;
      kommentar: string;
      entscheidungen: Record<string, Decision>;
    },
  ): Promise<CaseDetail>;
  downloadReport(
    id: string,
    format: 'xlsx' | 'pdf',
  ): Promise<{ file: Blob; fileName: string }>;
}

export const accessibilityApiRef = createApiRef<AccessibilityApi>({
  id: 'plugin.visionflow.accessibility',
});

export class AccessibilityClient implements AccessibilityApi {
  constructor(
    private readonly deps: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
  ) {}

  private async request(path: string, init?: RequestInit) {
    const base = `${await this.deps.discoveryApi.getBaseUrl(
      'visionflow',
    )}/accessibility`;
    const response = await this.deps.fetchApi.fetch(`${base}${path}`, init);
    if (!response.ok) {
      const body = await response.json().catch(() => undefined);
      throw new AccessibilityError(
        body?.error?.message ?? response.statusText,
        response.status,
        body?.error?.details ?? [],
      );
    }
    return response;
  }

  private json(method: string, body: unknown): RequestInit {
    return {
      method,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    };
  }

  async getAccess(): Promise<AccessibilityAccess> {
    return (await this.request('/access')).json();
  }

  async listCases(): Promise<CaseSummary[]> {
    return (await (await this.request('/cases')).json()).items;
  }

  async createCase(catalog: File, checklist: File, room: string) {
    const body = new FormData();
    body.append('catalog', catalog, catalog.name);
    body.append('checklist', checklist, checklist.name);
    if (room.trim()) body.append('room', room.trim());
    const response = await this.request('/cases', { method: 'POST', body });
    return (await response.json()).id as string;
  }

  async getCase(id: string): Promise<CaseDetail> {
    return (await this.request(`/cases/${encodeURIComponent(id)}`)).json();
  }

  async reanalyze(id: string) {
    await this.request(`/cases/${encodeURIComponent(id)}/reanalyze`, {
      method: 'POST',
    });
  }

  async saveReview(id: string, entscheidungen: Record<string, Decision>) {
    const response = await this.request(
      `/cases/${encodeURIComponent(id)}/review`,
      this.json('PUT', { entscheidungen }),
    );
    return response.json();
  }

  async signOff(
    id: string,
    input: {
      entscheidung: SignOff;
      kommentar: string;
      entscheidungen: Record<string, Decision>;
    },
  ) {
    const response = await this.request(
      `/cases/${encodeURIComponent(id)}/signoff`,
      this.json('POST', input),
    );
    return response.json();
  }

  async downloadReport(id: string, format: 'xlsx' | 'pdf') {
    const response = await this.request(
      `/cases/${encodeURIComponent(id)}/report.${format}`,
    );
    return {
      file: await response.blob(),
      fileName: parseFileName(
        response.headers.get('content-disposition'),
        `abnahme-barrierefreiheit.${format}`,
      ),
    };
  }
}

/** Same rule as the service and the backend: the worst verdict decides. */
export function recommendation(verdicts: Verdict[]): Recommendation {
  if (verdicts.includes('nicht_erfuellt')) return 'nicht_empfohlen';
  if (verdicts.includes('nicht_pruefbar')) return 'unter_vorbehalt';
  return 'empfohlen';
}
